
import PassengerDestination from "@/components/Passenger/models/PassengerDestination";
import Timetable from "@/components/Timetable/models/Timetable";
import Utility from './Utility';
import Group from './Group';
import IPassengerPhonNumberViewModel from "./IPassengerPhoneNumberViewModel";

export enum PassengerState {
    Normal = 0,
    Archived,
    Deleted
}

export class Passenger {
    // TODO: int, not number
    id: number = 0;
    movitId?: number | null = null;
    externalId: string;
    organizationId?: string = '';
    // TODO: int enum, not number
    state: PassengerState = PassengerState.Normal;
    groups?: string = "";
    yearGrade?: number | null = null;
    name: string | null = null;
    phone?: string = null;
    phoneNumbers: IPassengerPhonNumberViewModel[] = [];
    hasSsn: boolean = false;
    language?: string = null;
    note?: string = null;
    timitNote?: string = null;

    homeAddress?: string = null;
    homeCity?: string = null;
    homePostalcode?: string = null;

    pickupAddress?: string = null;
    timetables: Timetable[] = [];
    utilities?: string = null;
    
    billingName?: string = null;
    billingAddress?: string = null;
    billingPostalcode?: string = null;
    billingCity?: string = null;
    billingUseHomeAddress: boolean = true;

    inactive?: boolean = false;
    transportationPermissionRequired: boolean = false;
    transportationPermissionCount: number = 0;
    passengerDestinations: PassengerDestination[] = [];
    destinationIds: number[] = [];
    secondaryDestinationId?: number | null = null;
    movitInstanceId: number | null;
    hasPendingPropertyChanges: boolean = false;

    // Runtime only
    selected: boolean = false;
    picked: boolean = false;
    creator: boolean = false;

    constructor(options) {
        this.id = options?.id ?? 0;
        this.movitId = options?.movitId ?? null;
        this.externalId = options?.externalId ?? null;
        this.organizationId = options?.organizationId ?? null;
        this.state = options?.state ?? PassengerState.Normal;
        this.groups = options?.groups ?? "";
        this.yearGrade = options?.yearGrade ?? null;
        this.name = options?.name ?? null;
        this.phone = options?.phone ?? null;
        this.phoneNumbers = options?.phoneNumbers?.filter(p => !p.deletedAt && !p.deletedBy) ?? [];
        this.hasSsn = options && options.hasSsn;
        this.language = options?.language ?? null;
        this.note = options?.note ?? null;
        this.timitNote = options?.timitNote ?? null;

        this.homeAddress = options?.homeAddress ?? null;
        this.homeCity = options?.homeCity ?? null;
        this.homePostalcode = options?.homePostalcode ?? null;

        this.pickupAddress = options?.pickupAddress ?? null;
        this.timetables = options?.timetables ?? [];
        this.passengerDestinations = options?.passengerDestinations ?? [];
        this.destinationIds = options?.passengerDestinations?.map(d => d.destinationId) ?? [];
        this.secondaryDestinationId = options?.secondaryDestinationId ?? null;
        this.utilities = options?.utilities ?? "";
        
        this.billingName = options?.billingName ?? null;
        this.billingAddress = options?.billingAddress ?? null;
        this.billingPostalcode = options?.billingPostalcode ?? null;
        this.billingCity = options?.billingCity ?? null;
        this.billingUseHomeAddress = options?.billingUseHomeAddress ?? true;

        this.inactive = options?.inactive ?? false;
        this.transportationPermissionRequired = options?.transportationPermissionRequired ?? false;
        this.transportationPermissionCount = options?.transportationPermissionCount ?? 0;
        this.movitInstanceId = options?.movitInstanceId ?? null;
        this.hasPendingPropertyChanges = options?.hasPendingPropertyChanges ?? 0;

        this.selected = true;
        this.picked = false;
        this.creator = options && options.creator;
        
    }

    get isArchived(): boolean {
        return this.state === PassengerState.Archived;
    }

    get isDeleted(): boolean {
        return this.state === PassengerState.Deleted;
    }

    get isArchivedOrDeleted(): boolean {
        return this.isArchived || this.isDeleted;
    }

    get phoneNumbersList(): string[] {
        return this.phone
            ? this.phone.split(',').map(p => p.trim()).filter(p => p)
            : []
    }

    set phoneNumbersList(val){
        this.phone = val.join(', ');
    }

    get homeOrPickupAddress() {
        return this.pickupAddress 
            ? this.pickupAddress 
            : this.homeAddress;
    }

    // Note: Do not manipulate the utilityIds array, always re-write the entire array with the setter
    get utilityIds(): number[] {
        const splitUtilities = this.utilities 
            ? this.utilities.split(",").filter(u => u !== "")
            : [];
        return splitUtilities.map(uid => parseInt(uid, 10)).sort();
    }
    
    set utilityIds(val: number[]) {
       this.utilities = val.sort().join(",");
    }

    toggleUtility(utilityId: number){
        let utilityIds = this.utilityIds;
        if (utilityIds.some(id => id === utilityId)){
            utilityIds = utilityIds.filter(id => id !== utilityId);
        } else {
            utilityIds.push(utilityId);
        }
        this.utilityIds = utilityIds;
    }

    static CopyFrom(passenger: Passenger): Passenger {
        return new Passenger({
            id: passenger.id,
            movitId: passenger.movitId,
            externalId: passenger.externalId,
            organizationId: passenger.organizationId,
            state: passenger.state,
            groups: passenger.groups,
            yearGrade: passenger.yearGrade,
            name: passenger.name,
            phone: passenger.phone,
            phoneNumbers: passenger.phoneNumbers,
            hasSsn: passenger.hasSsn,
            language: passenger.language,
            note: passenger.note,
            timitNote: passenger.timitNote,
            homeAddress: passenger.homeAddress,
            homeCity: passenger.homeCity,
            homePostalcode: passenger.homePostalcode,
            pickupAddress: passenger.pickupAddress,
            timetables: passenger.timetables,
            passengerDestinations: passenger.passengerDestinations.map(d => d),
            movitInstanceId: passenger.movitInstanceId,
            utilities: passenger.utilities,
            billingName: passenger.billingName,
            billingAddress: passenger.billingAddress,
            billingPostalcode: passenger.billingPostalcode,
            billingCity: passenger.billingCity,
            billingUseHomeAddress: passenger.billingUseHomeAddress,
            hasPendingPropertyChanges: passenger.hasPendingPropertyChanges,
            inactive: passenger.inactive,
            selected: false,
            transportationPermissionRequired: passenger.transportationPermissionRequired,
            secondaryDestinationId: passenger.secondaryDestinationId,
        });
    }
    
    get groupObjs(): Group[]{
        return Group.getUniqueGroupNames(
            this.groups
            .split(",")
            .map(g => g.trim())
        ).map(g => new Group(g, false));
    }

    get groupNames(): string[]{
        const groupsString = this.groups ? this.groups : '';
        return Group.getUniqueGroupNames(
            groupsString
            .split(",")
            .map(g => g.trim())
        );
    }

    // Returns true if a property was modified, false if not
    updateGroups(groups: string[]){
        const newGroupsString = Group.getUniqueGroupNames(
            groups.map(g => g.trim())
        ).join(", ");

        if (newGroupsString === this.groups){
            // Groups are the same, no need to do anything
            return false;
        }
        // Set new groups
        this.groups = newGroupsString;
        return true;
    }

    // Returns true if a property was modified, false if not
    addToGroupNames(groups: string[]){
        const newGroupsString = Group.getUniqueGroupNames(
            this.groups
            .split(",")
            .map(g => g.trim())
            .concat(groups.map(g => g.trim())))
            .join(", ");
        if (newGroupsString === this.groups){
            // Groups are the same, no need to do anything
            return false;
        }
        // Set new groups
        this.groups = newGroupsString;
        return true;
    }

    // Returns true if a property was modified, false if not
    removeFromGroupNames(groups: string[]): boolean{
        const newGroupsString = Group.getUniqueGroupNames(
            this.groups
            .split(",")
            .map(g => g.trim())
            .filter(g => !groups.map(g2 => g2.trim()).includes(g)))
            .join(", ");
        if (newGroupsString === this.groups){
            // Groups are the same, no need to do anything
            return false;
        }
        // Set new groups
        this.groups = newGroupsString;
        return true;
    }

    get yearGradeFormatted(): string {
        return this.yearGrade ? this.yearGrade.toString() : "-";
    }

    get nameLowerCase(): string {
        return this.name.toLowerCase();
    }

    yearGradeMatchesSearch(search: string){
        if (search === "-" && this.yearGrade === 0){
            return true;
        }
        const searchAsInteger: number = parseInt(search, 10);
        return this.yearGrade === searchAsInteger;
    }

    nameMatchesSearch(search: string){
        const searchLowercase = search.toLowerCase();
        return this.nameLowerCase.startsWith(searchLowercase) || this.nameLowerCase.includes(" " + searchLowercase);
    }

    static utilities: Utility[] = [ 
        new Utility(1, "Wheelchair"),
        new Utility(2, "Receiver"),
        new Utility(3, "Single"),
        new Utility(4, "Walker"),
        new Utility(5, "Crutches"),
        new Utility(6, "Payer"),
        new Utility(7, "Safety seat"),
        new Utility(8, "Large car"),
        new Utility(9, "Small car"),
        new Utility(10, "Attendant"),
        new Utility(11, "Careva belt"),
        new Utility(12, "Stretcher"),
        new Utility(13, "Electric wheelchair"),
        new Utility(14, "Guide dog"),
        new Utility(15, "Folding wheelchair")
    ]

    public static sortByNameAlphabetically(passengers: Passenger[]): Passenger[] {
        return passengers.sort((a, b) => {
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();
            return (nameA < nameB) ? -1 : (nameA > nameB) ? 1 : 0;
        });
    }
}

export default Passenger;
